import {
  ComponentProps,
  useEffect,
  useState,
  useMemo,
  useCallback,
} from 'react';
import { useSearchParams } from 'react-router-dom';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { SortingState } from '@tanstack/react-table';
import { useTranslation } from 'react-i18next';

import {
  Flex,
  Header,
  OneColumnLayout,
  TabPanel,
  TabPanels,
  TabList,
  TabsContainer,
  spaces,
} from '@electricjs/arc';
import {
  AssetCondition,
  AssetStatus,
  ProductTypeName,
  SortOrder,
} from '@electricjs/core_entity-client';
import debounce from 'lodash/debounce';

import { SortDirections } from 'common/Table/types';
import { exportCSV } from 'common/utils/exportCsv';
import { useGlobalUI } from '@/components/GlobalUIProvider';
import { usePageTitle } from '@/contexts/PageTitleContext';
import MDMCertificateExpirationBanner from '@/components/MDMEnrollment/MDMCertificateExpirationBanner';
import PageSpinner from '@/components/PageSpinner';
import { DEFAULT_PAGE_SIZE } from '@/constants/pagination';
import { useGetOrganizationId } from '@/hooks/useGetOrganizationId';
import AssetsEmptyState from '@/pages/Assets/AssetsEmptyState';
import { getTranslatedAssetStatusLabel } from '@/pages/Assets/AssetStatus';
import getProductTypeLabel from '@/components/Assets/helpers/getProductTypeLabel';
import { getAssetConditionDisplayNameMap } from '@/components/Assets/helpers/transformOptions';
import {
  useLazyListAssetsV2Query,
  useListAssetsV2Query,
} from '@/redux/slices/assetApiSlice';
import {
  FilteringParamKeys,
  ModalParamKeys,
  NavigationParamKeys,
  PaginationParamKeys,
} from '@/types/queryParamKeys';
import AssetsTable, { AssetColumnIds } from '@/pages/Assets/AssetsTable';
import {
  AssetManagementTabs,
  AssetManagementTabType,
  IsDeviceFilterOption,
  Warranty,
} from '@/pages/Assets/types';
import AssetsCSVUploadModal from '@/components/Assets/AssetsCSVUploadModal';
import {
  AssetBackendSortField,
  getAssigneeCsvName,
  isValidAssetManagementTab,
  isValidOrderBy,
  mapOrderByToBackendField,
} from '@/pages/Assets/helpers';
import { StyledTab } from '@/components/common/StyledTab';
import {
  ASSET_NOT_ARCHIVED_STATUSES,
  ASSET_UNAVAILABLE_STATUSES,
} from '@/hooks/useGetEmployeesWithAssets';
import { useEmployeesMap } from '@/hooks/useEmployeesMap';

const defaultSortableColumn = AssetColumnIds.AssetName;

const Assets = () => {
  const { t } = useTranslation(['pages', 'assets']);
  const { showSuccessToast, showErrorToast } = useGlobalUI();
  const titleProps = usePageTitle(t('assetManagement.title'));
  const organizationId = useGetOrganizationId();
  const { ithDivisions } = useFlags();
  const { getEmployeeById, isEnabled, hasData } = useEmployeesMap();
  const isDivisionsEnabled = Boolean(ithDivisions);

  const [isExportingAssets, setIsExportingAssets] = useState<boolean>(false);

  const [queryParams, writeQueryParams] = useSearchParams();
  const [currentPage, setCurrentPage] = useState(
    parseInt(queryParams.get(PaginationParamKeys.Page) || '0')
  );
  const [searchTermFromParams, setSearchTermFromParams] = useState(
    queryParams.get(PaginationParamKeys.SearchTerm) || ''
  );
  const [assetStatus, setAssetStatus] = useState<AssetStatus[]>(
    (queryParams
      .get(FilteringParamKeys.AssetStatus)
      ?.split(',') as AssetStatus[]) || []
  );
  const [productType, setProductType] = useState<ProductTypeName[]>(
    (queryParams
      .get(FilteringParamKeys.ProductTypeName)
      ?.split(',') as ProductTypeName[]) || []
  );
  const [warranty, setWarranty] = useState<Warranty[]>(
    (queryParams.get(FilteringParamKeys.Warranty)?.split(',') as Warranty[]) ||
      []
  );
  const [excludeAssigned, setExcludeAssigned] = useState<boolean>(
    queryParams.get(FilteringParamKeys.ExcludeAssigned) === 'true'
  );
  const [isDevice, setIsDevice] = useState<IsDeviceFilterOption[]>(
    (queryParams
      .get(FilteringParamKeys.IsDevice)
      ?.split(',') as IsDeviceFilterOption[]) || []
  );
  const assignedToEmployeeId =
    queryParams.get(FilteringParamKeys.AssignedToEmployeeId) || '';

  const orderByFromParams = queryParams.get(PaginationParamKeys.OrderBy);
  const validatedOrderByFromParams =
    orderByFromParams && isValidOrderBy(orderByFromParams)
      ? orderByFromParams
      : defaultSortableColumn;
  const initialSorting = [
    {
      id: validatedOrderByFromParams,
      desc:
        queryParams.get(PaginationParamKeys.SortOrder) === SortDirections.Desc,
    },
  ];
  const [sorting, setSorting] = useState<SortingState>(initialSorting);

  const tabParam = queryParams.get(NavigationParamKeys.Tab);
  const selectedTab = isValidAssetManagementTab(tabParam)
    ? tabParam
    : AssetManagementTabs.ASSIGNED;

  const getAssetQueryParams = useCallback(
    (
      tab: AssetManagementTabType,
      options: { includeFilterParams?: boolean } = {}
    ) => {
      const { includeFilterParams = true } = options;

      // Validate orderBy to prevent invalid values from being passed to the API
      const sortingOrderBy = sorting[0]?.id;
      const validatedOrderBy = isValidOrderBy(sortingOrderBy)
        ? sortingOrderBy
        : defaultSortableColumn;
      const orderBy = mapOrderByToBackendField(validatedOrderBy);

      const baseParams = {
        organizationId,
        limit: DEFAULT_PAGE_SIZE,
        offset: currentPage * DEFAULT_PAGE_SIZE,
        orderBy,
        sortOrder: sorting[0]?.desc ? SortOrder.Desc : SortOrder.Asc,
      };

      const filterParams = {
        searchTerm: searchTermFromParams,
        statuses: assetStatus,
        productTypes: productType,
        warrantyStatus: warranty.length > 0 ? warranty : undefined,
        isDevice:
          isDevice.length > 0
            ? isDevice.includes(IsDeviceFilterOption.YES)
            : undefined,
        assignedTo: assignedToEmployeeId || undefined,
      };

      if (tab === AssetManagementTabs.ASSIGNED) {
        return {
          ...baseParams,
          ...(includeFilterParams ? filterParams : {}),
          hasAssignee: true,
          statuses:
            assetStatus.length > 0 && includeFilterParams
              ? assetStatus
              : Array.from(ASSET_NOT_ARCHIVED_STATUSES),
        };
      } else if (tab === AssetManagementTabs.UNASSIGNED) {
        return {
          ...baseParams,
          ...(includeFilterParams ? filterParams : {}),
          hasAssignee: false,
          statuses:
            assetStatus.length > 0 && includeFilterParams
              ? assetStatus
              : Array.from(ASSET_NOT_ARCHIVED_STATUSES),
        };
      } else if (tab === AssetManagementTabs.ARCHIVED) {
        return {
          ...baseParams,
          ...(includeFilterParams ? filterParams : {}),
          statuses:
            assetStatus.length > 0 && includeFilterParams
              ? assetStatus
              : Array.from(ASSET_UNAVAILABLE_STATUSES),
        };
      }

      return baseParams;
    },
    [
      organizationId,
      currentPage,
      sorting,
      searchTermFromParams,
      assetStatus,
      productType,
      warranty,
      isDevice,
      assignedToEmployeeId,
    ]
  );

  // Count queries use their own params rather than delegating to getAssetQueryParams.
  // getAssetQueryParams closes over `sorting` and `currentPage` in its useCallback
  // deps, so any query that calls it would refetch on every sort/page change — even
  // though counts are unaffected by those. Keeping count params independent prevents
  // three unnecessary API calls on every sort or pagination interaction.
  const getAssetCountQueryParams = useCallback(
    (tab: AssetManagementTabType) => ({
      organizationId,
      limit: 0,
      offset: 0,
      orderBy: 'name' as AssetBackendSortField,
      sortOrder: SortOrder.Asc,
      ...(tab === AssetManagementTabs.ASSIGNED && { hasAssignee: true }),
      ...(tab === AssetManagementTabs.UNASSIGNED && { hasAssignee: false }),
      statuses:
        tab === AssetManagementTabs.ARCHIVED
          ? Array.from(ASSET_UNAVAILABLE_STATUSES)
          : Array.from(ASSET_NOT_ARCHIVED_STATUSES),
    }),
    [organizationId]
  );

  // COUNTS ONLY - Fetch counts for all tabs with minimal data transfer
  const { data: { total: totalAssignedAssetsFilteredByBackend } = {} } =
    useListAssetsV2Query(
      getAssetCountQueryParams(AssetManagementTabs.ASSIGNED)
    );

  const { data: { total: totalUnassignedAssetsFilteredByBackend } = {} } =
    useListAssetsV2Query(
      getAssetCountQueryParams(AssetManagementTabs.UNASSIGNED)
    );

  const { data: { total: totalArchivedAssetsFilteredByBackend } = {} } =
    useListAssetsV2Query(
      getAssetCountQueryParams(AssetManagementTabs.ARCHIVED)
    );

  // FULL DATA - Fetch full paginated data only for the active tab
  const assetParams = useMemo(
    () => getAssetQueryParams(selectedTab),
    [getAssetQueryParams, selectedTab]
  );
  const {
    data: {
      results: assetsFilteredByBackend,
      total: totalFilteredByBackend,
    } = {},
    isFetching: isFetchingFilteredByBackend,
    isLoading: isLoadingFilteredByBackend,
  } = useListAssetsV2Query(assetParams);

  const handleManualSort = (orderBy: string, sortOrder: SortOrder) => {
    setCurrentPage(0);
    setSorting([{ id: orderBy, desc: sortOrder === SortOrder.Desc }]);
  };

  const handleTabChange = (tab: AssetManagementTabType) => {
    setCurrentPage(0);
    writeQueryParams(
      {
        [NavigationParamKeys.Tab]: tab,
      },
      { replace: true }
    );
  };

  // This filter is for Asset Status
  const handleAssetStatusFilter = (
    assetStatus: AssetStatus[] | ((prev: AssetStatus[]) => AssetStatus[])
  ) => {
    setAssetStatus(assetStatus);
    setCurrentPage(0);
  };

  const handleProductTypeFilter = (
    productType:
      | ProductTypeName[]
      | ((prev: ProductTypeName[]) => ProductTypeName[])
  ) => {
    setProductType(productType);
    setCurrentPage(0);
  };

  const handleWarrantyFilter = (
    warranty: Warranty[] | ((prev: Warranty[]) => Warranty[])
  ) => {
    setWarranty(warranty);
    setCurrentPage(0);
  };

  const handleIsDeviceFilter = (
    isDevice:
      | IsDeviceFilterOption[]
      | ((prev: IsDeviceFilterOption[]) => IsDeviceFilterOption[])
  ) => {
    setIsDevice(isDevice);
    setCurrentPage(0);
  };

  useEffect(() => {
    // Prevents the `page` url parameter from setting a page greater than the last possible one
    // NOTE: We can eventually move this logic to Arc to avoid repetition
    if (isFetchingFilteredByBackend) return;

    const pageQueryParam = queryParams.get(PaginationParamKeys.Page);
    if (!pageQueryParam) return;

    const pageFromParams = parseInt(pageQueryParam);
    const lastPage = Math.floor(
      (totalFilteredByBackend ?? 0) / DEFAULT_PAGE_SIZE
    );
    if (pageFromParams <= lastPage) return;

    setCurrentPage(lastPage);
  }, [totalFilteredByBackend, isFetchingFilteredByBackend, queryParams]);

  useEffect(() => {
    queryParams.set(PaginationParamKeys.Page, currentPage.toString());
    queryParams.set(PaginationParamKeys.SearchTerm, searchTermFromParams || '');
    queryParams.set(PaginationParamKeys.OrderBy, sorting[0].id);
    queryParams.set(
      PaginationParamKeys.SortOrder,
      sorting[0].desc ? SortDirections.Desc : SortDirections.Asc
    );
    if (assetStatus.length > 0) {
      queryParams.set(FilteringParamKeys.AssetStatus, assetStatus.join(','));
    } else {
      queryParams.delete(FilteringParamKeys.AssetStatus);
    }
    if (productType.length > 0) {
      queryParams.set(
        FilteringParamKeys.ProductTypeName,
        productType.join(',')
      );
    } else {
      queryParams.delete(FilteringParamKeys.ProductTypeName);
    }
    if (warranty.length > 0) {
      queryParams.set(FilteringParamKeys.Warranty, warranty.join(','));
    } else {
      queryParams.delete(FilteringParamKeys.Warranty);
    }
    if (isDevice.length > 0) {
      queryParams.set(FilteringParamKeys.IsDevice, isDevice.join(','));
    } else {
      queryParams.delete(FilteringParamKeys.IsDevice);
    }

    // The exclude assigned filter can only be set through url params.
    // This sets the tab to unassigned if the exclude assigned filter is true.
    if (excludeAssigned) {
      queryParams.set(NavigationParamKeys.Tab, AssetManagementTabs.UNASSIGNED);
    } else {
      queryParams.delete(FilteringParamKeys.ExcludeAssigned);
    }

    if (assignedToEmployeeId) {
      queryParams.set(
        FilteringParamKeys.AssignedToEmployeeId,
        assignedToEmployeeId
      );
    } else {
      queryParams.delete(FilteringParamKeys.AssignedToEmployeeId);
    }

    writeQueryParams(queryParams, { replace: true });
  }, [
    currentPage,
    searchTermFromParams,
    sorting,
    assetStatus,
    productType,
    warranty,
    isDevice,
    assignedToEmployeeId,
    queryParams,
    writeQueryParams,
    excludeAssigned,
  ]);

  const showAssetsEmptyState =
    !assetsFilteredByBackend &&
    !isFetchingFilteredByBackend &&
    !isLoadingFilteredByBackend;

  // This is used to fetch all assets without filters for exporting
  const [listAssets] = useLazyListAssetsV2Query();

  const handleExport = async () => {
    setIsExportingAssets(true);

    try {
      // Without the map every assignee would be exported as "Restricted".
      if (isEnabled && !hasData) {
        throw new Error('Employees map not loaded');
      }

      const fullAssetsData = await listAssets(
        {
          organizationId: organizationId,
          orderBy: 'name',
          sortOrder: 'asc' as SortOrder,
        },
        true
      ).unwrap();

      const assetConditionDisplayNameMap = getAssetConditionDisplayNameMap(t);
      const assetsCSVData = fullAssetsData?.results?.map(asset => ({
        [t('assets:assetsPage.csvColumns.name')]: asset.name,
        [t('assets:assetsPage.csvColumns.assetId')]: asset.customAssetId,
        [t('assets:assetsPage.csvColumns.type')]: getProductTypeLabel(
          asset.productType.name as ProductTypeName
        ),
        [t('assets:assetsPage.csvColumns.site')]: asset.site?.name,
        [t('assets:assetsPage.csvColumns.assignedTo')]: getAssigneeCsvName(
          asset,
          t,
          { isDivisionsEnabled, getEmployeeById }
        ),
        [t('assets:assetsPage.csvColumns.status')]:
          getTranslatedAssetStatusLabel(asset.status as AssetStatus, t),
        [t('assets:assetsPage.csvColumns.serialNumber')]: asset.serialNumber,
        [t('assets:assetsPage.csvColumns.purchaseDate')]: asset.purchaseDate,
        [t('assets:assetsPage.csvColumns.purchasePrice')]:
          asset.purchasePrice?.toString(),
        [t('assets:assetsPage.csvColumns.purchasePriceCurrencyCode')]:
          asset.purchasePriceCurrencyCode,
        [t('assets:assetsPage.csvColumns.purchasedFrom')]:
          asset.purchasedFromUrl,
        [t('assets:assetsPage.csvColumns.brand')]: asset.brand,
        [t('assets:assetsPage.csvColumns.model')]: asset.model,
        [t('assets:assetsPage.csvColumns.notes')]: asset.notes,
        [t('assets:assetsPage.csvColumns.condition')]: asset.condition
          ? (assetConditionDisplayNameMap[asset.condition as AssetCondition] ??
            asset.condition)
          : '',
        [t('assets:assetsPage.csvColumns.warrantyExpiration')]:
          asset.warrantyExpirationDate,
        [t('assets:assetsPage.csvColumns.image')]: asset.imageUrl,
        [t('assets:assetsPage.csvColumns.deviceId')]: asset.device?.id,
      }));

      exportCSV(assetsCSVData, 'assets-list.csv');

      showSuccessToast({
        id: 'assets-export-success-toast',
        message: t('assets:assetsPage.exportSuccess'),
        stack: true,
      });
    } catch (error) {
      console.error('Unable to export assets list', error);
      showErrorToast({
        id: 'assets-export-error-toast',
        message: t('assets:assetsPage.exportError'),
        stack: true,
      });
    } finally {
      setIsExportingAssets(false);
    }
  };

  const debouncedSetSearchTerm = useMemo(
    () => debounce(setSearchTermFromParams, 500),
    [setSearchTermFromParams]
  );

  const handleCloseModal = () => {
    const newParams = new URLSearchParams(queryParams);
    newParams.delete(NavigationParamKeys.Modal);
    writeQueryParams(newParams, { replace: true });
  };

  const isAssetsCsvUploadModalOpen =
    queryParams.get(NavigationParamKeys.Modal) ===
    ModalParamKeys.AssetsCsvUpload;

  if (isLoadingFilteredByBackend) {
    return <PageSpinner />;
  }

  if (showAssetsEmptyState) {
    return <AssetsEmptyState />;
  }

  if (!assetsFilteredByBackend) {
    return null;
  }

  const assetsTableProps: ComponentProps<typeof AssetsTable> = {
    assets: assetsFilteredByBackend ?? [],
    totalAssets: totalFilteredByBackend ?? 0,
    assetsCount: assetsFilteredByBackend?.length ?? 0,
    isFetchingAssets: isFetchingFilteredByBackend,
    handleExport,
    isExportingAssets,
    setCurrentPage,
    currentPage,
    searchTerm: searchTermFromParams,
    setSearchTerm: debouncedSetSearchTerm,
    handleAssetStatusFilter,
    assetStatus,
    handleProductTypeFilter,
    productType,
    handleWarrantyFilter,
    warranty,
    handleIsDeviceFilter,
    isDevice,
    sorting,
    onManualSort: handleManualSort,
    pageSize: DEFAULT_PAGE_SIZE,
    setExcludeAssigned,
  };

  return (
    <OneColumnLayout>
      <MDMCertificateExpirationBanner />
      {/* TODO: [Refresh] Remove if we decide to remove all subtitles, include removing translations */}
      <Header {...titleProps} />
      <Flex vertical gap={spaces.space_m} width="100%">
        <TabsContainer selectedId={selectedTab}>
          <TabList>
            <StyledTab
              id={AssetManagementTabs.ASSIGNED}
              onClick={() => handleTabChange(AssetManagementTabs.ASSIGNED)}
              count={totalAssignedAssetsFilteredByBackend}
              width="250px">
              {t('assets:assetsPage.assignedAssets')}
            </StyledTab>
            <StyledTab
              id={AssetManagementTabs.UNASSIGNED}
              onClick={() => handleTabChange(AssetManagementTabs.UNASSIGNED)}
              count={totalUnassignedAssetsFilteredByBackend}
              width="250px">
              {t('assets:assetsPage.unassignedAssets')}
            </StyledTab>
            <StyledTab
              id={AssetManagementTabs.ARCHIVED}
              onClick={() => handleTabChange(AssetManagementTabs.ARCHIVED)}
              count={totalArchivedAssetsFilteredByBackend}
              width="250px">
              {t('assets:assetsPage.archivedAssets')}
            </StyledTab>
          </TabList>
          <TabPanels width="100%">
            <TabPanel id={AssetManagementTabs.ASSIGNED}>
              <AssetsTable {...assetsTableProps} />
            </TabPanel>
            <TabPanel id={AssetManagementTabs.UNASSIGNED}>
              <AssetsTable {...assetsTableProps} />
            </TabPanel>
            <TabPanel id={AssetManagementTabs.ARCHIVED}>
              <AssetsTable {...assetsTableProps} />
            </TabPanel>
          </TabPanels>
        </TabsContainer>
      </Flex>

      {isAssetsCsvUploadModalOpen && (
        <AssetsCSVUploadModal
          onCancel={handleCloseModal}
          onSuccess={handleCloseModal}
        />
      )}
    </OneColumnLayout>
  );
};

export default Assets;
